"use client";

import React, { useEffect, useRef } from "react";
import Link from "next/link";

const painPoints = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    problem: "Unexpected Downtime",
    solution: "Proactive monitoring catches issues before they impact your business.",
    accent: "#3B82F6",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    problem: "Cybersecurity Risk",
    solution: "Multi-layered protection across users, devices, and your entire network.",
    accent: "#22D3EE",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
        <path d="M8 10l2 2 4-4"/>
      </svg>
    ),
    problem: "Slow & Unreliable Systems",
    solution: "Optimised infrastructure and endpoint management keeps your team productive.",
    accent: "#60A5FA",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    problem: "Remote Work Challenges",
    solution: "Secure remote access, cloud tools, and unified communications for distributed teams.",
    accent: "#60A5FA",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    problem: "Unpredictable IT Costs",
    solution: "Fixed monthly retainers with transparent pricing — no surprise bills.",
    accent: "#A78BFA",
  },
];

const conversionCTAs = [
  {
    title: "IT Health Check",
    description: "Get a comprehensive review of your current IT environment and identify gaps.",
    cta: "Book Free Assessment",
    href: "/book",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    accent: "#3B82F6",
    accentBg: "rgba(59,130,246,0.12)",
    featured: true,
  },
  {
    title: "Free Consultation",
    description: "Talk to an IT specialist about your business needs — no obligation.",
    cta: "Book a Call",
    href: "/book",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    accent: "#22D3EE",
    accentBg: "rgba(34,211,238,0.1)",
    featured: false,
  },
  {
    title: "Cybersecurity Assessment",
    description: "Identify vulnerabilities in your current security posture before attackers do.",
    cta: "Get Assessment",
    href: "/contact",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <path d="M9 12l2 2 4-4"/>
      </svg>
    ),
    accent: "#60A5FA",
    accentBg: "rgba(96,165,250,0.1)",
    featured: false,
  },
];

export default function SMBPainPointsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll(".pain-reveal");
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden-reveal");
            entry.target.classList.add("animate-reveal");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items?.forEach((item) => {
      item?.classList?.add("hidden-reveal", "reveal");
      observer?.observe(item);
    });
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="pt-12 pb-14 sm:pt-12 sm:pb-16"
      style={{ background: "#070B14" }}
    >
      <div className="max-w-container mx-auto px-4 sm:px-6 w-full">

        {/* Section header */}
        <div className="pain-reveal text-center mb-14">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-6"
            style={{
              border: "1px solid rgba(59,130,246,0.2)",
              background: "rgba(37,99,235,0.07)",
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#3B82F6" }} />
            <span className="text-[11px] font-heading font-600 tracking-widest uppercase gradient-text">
              Built for SMBs
            </span>
          </div>
          <h2
            className="font-heading font-700 leading-tight mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 42px)", color: "#F8FAFC" }}
          >
            We Solve the IT Challenges{' '}
            <br className="hidden sm:block" />
            <span className="gradient-text">Growing Businesses Face Every Day</span>
          </h2>
          <p className="text-[15px] font-body max-w-[560px] mx-auto leading-relaxed" style={{ color: "#94A3B8" }}>
            Designed for businesses with 10–100 users, multiple locations, and no internal IT team.
            Pro IT Solutions becomes your dedicated technology partner.
          </p>
        </div>

        {/* Pain points grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-16">
          {painPoints?.map((point, i) => (
            <div
              key={point?.problem}
              className={`pain-reveal stagger-${i + 1} relative p-5 rounded-xl flex flex-col gap-3`}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${point?.accent}20`, color: point?.accent, opacity: 0.9 }}
              >
                {point?.icon}
              </div>
              <div>
                <p className="font-heading font-700 text-[14px] mb-1.5" style={{ color: "#FFFFFF" }}>
                  {point?.problem}
                </p>
                <p className="text-[14px] font-body leading-relaxed leading-[1.75]" style={{ color: "#CBD5E1" }}>
                  {point?.solution}
                </p>
              </div>
              {/* Accent bottom line */}
              <div
                className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full opacity-40"
                style={{ background: `linear-gradient(90deg, ${point?.accent}, transparent)` }}
              />
            </div>
          ))}
        </div>

        {/* Conversion CTAs */}
        <div className="pain-reveal">
          <p
            className="text-center text-[11px] font-heading font-600 uppercase tracking-widest mb-6"
            style={{ color: "#64748B" }}
          >
            Get Started — No Obligation
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {conversionCTAs?.map((cta) => (
              <Link
                key={cta?.title}
                href={cta?.href}
                className="group relative overflow-hidden rounded-xl p-6 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: cta?.featured
                    ? "linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(11,18,32,0.95) 100%)"
                    : "rgba(255,255,255,0.03)",
                  border: cta?.featured
                    ? "1px solid rgba(59,130,246,0.3)"
                    : "1px solid rgba(255,255,255,0.07)",
                  boxShadow: cta?.featured ? "0 0 30px rgba(37,99,235,0.1)" : "none",
                }}
              >
                {/* Hover glow */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                  style={{ background: `radial-gradient(ellipse at 30% 30%, ${cta?.accentBg} 0%, transparent 60%)` }}
                />
                <div
                  className="relative z-10 w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: cta?.accentBg, color: cta?.accent }}
                >
                  {cta?.icon}
                </div>
                <div className="relative z-10 flex-1">
                  <h3 className="font-heading font-600 text-[16px] mb-1.5" style={{ color: "#F8FAFC" }}>
                    {cta?.title}
                  </h3>
                  <p className="text-[14px] font-body leading-relaxed leading-[1.75]" style={{ color: "#94A3B8" }}>
                    {cta?.description}
                  </p>
                </div>
                <div
                  className="relative z-10 inline-flex items-center gap-2 text-[13px] font-heading font-600 transition-all duration-300 group-hover:gap-3"
                  style={{ color: cta?.accent }}
                >
                  {cta?.cta}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
